import React, { useState, useEffect } from 'react';
import { Sparkles, Clock, Bell, Users, Heart, MessageSquare, Lock, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

// December 1st, 2026 at 00:00 Europe/London
// The UK will be on GMT (UTC+0) on that date (BST ends late October)
const LAUNCH_DATE = new Date('2026-12-01T00:00:00+00:00');

const useCountdown = (target) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, target.getTime() - now);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);
  return { days, hours, minutes, seconds, launched: diff === 0 };
};

const FEATURES = [
  { icon: Users,         title: 'Live Pulse',      desc: 'See who\'s online and what the community is buzzing about — right now.' },
  { icon: MessageSquare, title: 'Threaded Posts',  desc: 'Start a conversation, share media, quote your favourite reactions.' },
  { icon: Heart,         title: 'Rich Reactions',  desc: 'More than a like — celebrate, cheer, hug, or fire it up.' },
  { icon: Sparkles,      title: 'Members Only',    desc: 'A curated space just for signed-in Starnight members. No noise.' },
];

const LoungeComingSoon = () => {
  const { days, hours, minutes, seconds, launched } = useCountdown(LAUNCH_DATE);
  const [notified, setNotified] = useState(false);

  const setReminder = () => {
    if (notified) return;
    setNotified(true);
    toast.success("We'll ping you the moment the doors open.", {
      description: 'The Lounge opens 1 Dec 2026 · 12:00 AM UK.',
    });
  };

  return (
    <div className="relative overflow-hidden rounded-3xl border border-white/[0.06] bg-gradient-to-br from-[#0A0A14] via-[#0A0A14] to-[#0F0A1F]" data-testid="lounge-coming-soon">
      {/* Ambient orbs */}
      <div className="pointer-events-none absolute -top-32 -left-16 w-[380px] h-[380px] bg-[#8A2BE2]/[0.09] rounded-full blur-[110px]" />
      <div className="pointer-events-none absolute -bottom-24 -right-16 w-[420px] h-[300px] bg-[#00E5FF]/[0.07] rounded-full blur-[120px]" />
      {/* Grain */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.03] mix-blend-overlay"
        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")` }} />

      <div className="relative px-8 md:px-12 py-12 md:py-16">
        {/* Kicker */}
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#00E5FF]/25 bg-[#00E5FF]/[0.06] mb-8" data-testid="lounge-kicker">
          <span className="w-1.5 h-1.5 bg-[#00E5FF] rounded-full animate-pulse" />
          <span className="text-[10px] tracking-[0.25em] uppercase text-[#00E5FF] font-medium">Members Only · Coming Soon</span>
        </div>

        {/* Headline */}
        <div className="max-w-2xl">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-light tracking-tighter text-white mb-4 leading-[1.05]">
            The <span className="bg-gradient-to-br from-[#00E5FF] via-[#8A2BE2] to-[#c39be5] bg-clip-text text-transparent font-medium">Lounge</span> opens
            <br />
            <span className="text-white/50 text-3xl md:text-4xl lg:text-5xl">1 December 2026 · 12:00 AM UK</span>
          </h1>
          <p className="text-white/50 text-sm md:text-base leading-relaxed max-w-lg">
            A private space for Starnight members to share, react, and hang out. We're putting the finishing touches on the details — hold tight.
          </p>
        </div>

        {/* Countdown */}
        <div className="mt-10 grid grid-cols-4 gap-2 md:gap-4 max-w-xl" data-testid="lounge-countdown">
          {[
            { label: 'Days',    value: days },
            { label: 'Hours',   value: hours },
            { label: 'Minutes', value: minutes },
            { label: 'Seconds', value: seconds },
          ].map((u) => (
            <div key={u.label} className="relative rounded-2xl border border-white/[0.08] bg-white/[0.02] px-3 py-4 md:px-5 md:py-6 text-center overflow-hidden"
              data-testid={`countdown-${u.label.toLowerCase()}`}>
              <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#00E5FF]/40 to-transparent" />
              <div className="text-3xl md:text-5xl font-light text-white tabular-nums tracking-tight" style={{ fontFeatureSettings: '"tnum"' }}>
                {String(u.value).padStart(2, '0')}
              </div>
              <div className="text-[9px] md:text-[10px] tracking-[0.2em] uppercase text-white/30 font-medium mt-1">{u.label}</div>
            </div>
          ))}
        </div>

        {launched && (
          <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/[0.10] border border-emerald-500/25">
            <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
            <span className="text-emerald-300 text-[11px] font-medium tracking-[0.1em] uppercase">Now Live — refresh to enter</span>
          </div>
        )}

        {/* CTAs */}
        <div className="mt-10 flex flex-wrap items-center gap-3">
          <Button
            onClick={setReminder}
            disabled={notified}
            data-testid="lounge-notify-btn"
            className="bg-white text-black hover:bg-white/90 font-medium px-6 py-3 rounded-full text-xs tracking-[0.1em] uppercase transition-all disabled:opacity-60"
          >
            {notified ? (<>Reminder set <Sparkles className="w-3.5 h-3.5 ml-2" /></>) : (<><Bell className="w-3.5 h-3.5 mr-2" /> Notify me on launch</>)}
          </Button>
          <div className="flex items-center gap-1.5 text-[11px] text-white/30">
            <Lock className="w-3 h-3" />
            <span>Reserved for signed-in members</span>
          </div>
        </div>

        {/* Feature preview */}
        <div className="mt-14 pt-10 border-t border-white/[0.05]">
          <p className="text-[10px] tracking-[0.25em] uppercase text-white/30 font-medium mb-6">Here's a taste</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} data-testid={`lounge-feature-${i}`}
                  className="group flex items-start gap-4 p-4 rounded-2xl border border-white/[0.05] bg-white/[0.015] hover:border-white/[0.10] hover:bg-white/[0.03] transition-all">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00E5FF]/[0.10] to-[#8A2BE2]/[0.10] border border-white/[0.06] flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                    <Icon className="w-4 h-4 text-[#00E5FF]" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium">{f.title}</p>
                    <p className="text-white/45 text-xs mt-1 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Sign-off */}
        <div className="mt-10 flex items-center gap-2 text-white/25 text-[10px] tracking-[0.15em] uppercase">
          <Clock className="w-3 h-3" />
          <span>Launch: 1 Dec 2026 · 00:00 GMT (UK)</span>
        </div>
      </div>
    </div>
  );
};

export default LoungeComingSoon;
